import { ArrowLeftIcon, CheckIcon, ClockIcon, CopyIcon, LinkIcon, RadioIcon, TriangleAlertIcon, UserRoundIcon } from 'lucide-react';
import { useState, type ReactNode } from 'react';
import { Link, useParams } from 'react-router';
import { DayStamp, Incoming, PhoneFrame, SmsComposeBar, SmsHeader, SmsThread } from '@/components/brand/PhoneConversation';
import { ErrorState } from '@/components/ErrorState';
import { MessageStatusBadge, RecipientStatusBadge } from '@/components/StatusBadge';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { TokenText } from '@/features/templates/TokenText';
import { useJobContext } from '@/layouts/JobLayout';
import { fmtDateTime, fmtNumber, fmtRelative } from '@/lib/format';
import type { RecipientStatus } from '@/lib/types';
import { cn } from '@/lib/utils';
import { useRecipient } from './queries';

/** How the carrier gateway is named to people; unknown providers show as recorded. */
export function providerLabel(provider: string | null | undefined): string {
  if (!provider) return 'Not sent';
  if (provider === 'mock') return 'Mock provider';
  if (provider === 'twilio') return 'Twilio';
  if (provider === 'none') return 'No messaging account';
  return provider.charAt(0).toUpperCase() + provider.slice(1);
}

const OUTCOME: Record<RecipientStatus, { title: string; detail: string; tone: 'ok' | 'bad' | 'wait' | 'skip' }> = {
  queued: { title: 'Waiting to send', detail: 'In the queue; the gateway has not taken it yet.', tone: 'wait' },
  sending: { title: 'Handing to the carrier', detail: 'The gateway is accepting it now.', tone: 'wait' },
  sent: { title: 'Accepted by the carrier', detail: 'Sent, waiting for the delivery receipt. Receipts can take a few minutes.', tone: 'wait' },
  delivered: { title: 'Delivered to the handset', detail: 'The carrier confirmed the phone received it.', tone: 'ok' },
  undelivered: { title: 'Undelivered', detail: 'The carrier accepted it but could not deliver it to the phone.', tone: 'bad' },
  failed: { title: 'Failed', detail: 'The gateway rejected it or the send threw an error.', tone: 'bad' },
  skipped_opted_out: { title: 'Skipped: opted out', detail: 'This person replied STOP or was marked opted out, so nothing was sent.', tone: 'skip' },
  skipped_invalid: { title: 'Skipped: invalid number', detail: 'The number could not be dialled, so nothing was sent.', tone: 'skip' },
};

/**
 * One person's receipt at /jobs/:jobId/history/:messageId/recipients/:recipientId: the exact text they were sent,
 * drawn on a phone; the outcome and the timeline; and the carrier record (provider, account, message id,
 * attempts) so a delivery can be verified against the provider's own logs.
 */
export function RecipientPage() {
  const { job } = useJobContext();
  const { messageId = '', recipientId = '' } = useParams();
  const q = useRecipient(job.id, messageId, recipientId);

  if (q.isPending) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-6 w-40" />
        <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_22rem]">
          <Skeleton className="h-72" />
          <Skeleton className="h-72" />
        </div>
      </div>
    );
  }
  if (q.isError) return <ErrorState error={q.error} onRetry={() => void q.refetch()} title="Could not load this receipt" />;

  const { recipient: r, message: m } = q.data;
  const outcome = OUTCOME[r.status];
  const live = r.status === 'queued' || r.status === 'sending' || r.status === 'sent';
  const clock = new Intl.DateTimeFormat(undefined, { hour: 'numeric', minute: '2-digit' }).format(new Date(r.sentAt ?? r.queuedAt)).replace(/\s?[AP]M$/i, '');
  const steps: Array<{ label: string; at: string | null; done: boolean; bad?: boolean }> = [
    { label: 'Queued', at: r.queuedAt, done: true },
    { label: r.status === 'skipped_opted_out' || r.status === 'skipped_invalid' ? 'Skipped' : r.status === 'failed' && !r.sentAt ? 'Rejected by the gateway' : 'Sent to carrier', at: r.sentAt ?? (r.status.startsWith('skipped') || r.status === 'failed' ? r.failedAt : null), done: !!r.sentAt || r.status.startsWith('skipped') || r.status === 'failed', bad: r.status === 'failed' && !r.sentAt },
    // Skipped rows and rows the gateway refused never had a delivery leg.
    ...(r.status.startsWith('skipped') || (r.status === 'failed' && !r.sentAt) ? [] : [{ label: r.status === 'undelivered' || (r.status === 'failed' && !!r.sentAt) ? 'Delivery failed' : 'Delivered', at: r.deliveredAt ?? (r.status === 'undelivered' || r.status === 'failed' ? r.failedAt : null), done: !!r.deliveredAt || r.status === 'undelivered' || (r.status === 'failed' && !!r.sentAt), bad: r.status === 'undelivered' || r.status === 'failed' }]),
  ];

  return (
    <div className="space-y-5 sm:space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <Button asChild variant="ghost" size="sm" className="-ml-2">
          <Link to={`/jobs/${job.id}/history/${m.id}`}>
            <ArrowLeftIcon /> Message
          </Link>
        </Button>
        <div className="flex flex-wrap items-center gap-2">
          {r.contactId && (
            <Button asChild variant="outline" size="sm">
              <Link to={`/jobs/${job.id}/contacts/${r.contactId}`}>
                <UserRoundIcon /> Open contact
              </Link>
            </Button>
          )}
          <CopyLink />
        </div>
      </div>

      {/* ------------------------------------------------------------ who and how it went */}
      <header className="bg-card border px-5 py-4">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div className="min-w-0">
            <p className="text-muted-foreground text-[11px] font-medium tracking-wide uppercase">Receipt</p>
            <h1 className="font-display mt-1 text-[1.75rem] leading-none tracking-tight md:text-[2rem]">{r.contactName ?? r.phoneDisplay}</h1>
            <p className="text-muted-foreground mt-2 text-sm tabular-nums">
              {r.phoneDisplay}
              {r.language ? ` · ${r.language.toUpperCase()}` : ''}
              {' · '}
              <span title={fmtDateTime(r.queuedAt)}>queued {fmtRelative(r.queuedAt)}</span>
            </p>
          </div>
          <div className="flex items-center gap-2">
            {live && (
              <span className="text-muted-foreground flex items-center gap-1.5 text-xs">
                <span className="bg-primary size-1.5 animate-pulse rounded-full" aria-hidden /> updating
              </span>
            )}
            <RecipientStatusBadge status={r.status} />
          </div>
        </div>
        <div className={cn('mt-4 flex items-start gap-3 border-l-2 pl-3', outcome.tone === 'ok' ? 'border-success' : outcome.tone === 'bad' ? 'border-destructive' : outcome.tone === 'wait' ? 'border-primary' : 'border-muted-foreground/40')}>
          <div className="min-w-0">
            <p className="text-sm font-medium">{outcome.title}</p>
            <p className="text-muted-foreground text-sm">{outcome.detail}</p>
            {r.errorCode && (
              <p className="text-destructive mt-1.5 flex items-start gap-1.5 text-sm">
                <TriangleAlertIcon className="mt-0.5 size-3.5 shrink-0" aria-hidden />
                <span>
                  <span className="font-mono text-xs">{r.errorCode}</span>
                  {r.errorMessage ? ` · ${r.errorMessage}` : ''}
                </span>
              </p>
            )}
          </div>
        </div>
      </header>

      <div className="grid gap-5 sm:gap-6 lg:grid-cols-[minmax(0,1fr)_22rem] lg:items-start">
        <div className="min-w-0 space-y-5 sm:space-y-6">
          {/* ------------------------------------------------------------ the exact text */}
          <section className="bg-card border" aria-label="Text sent">
            <div className="flex flex-wrap items-center justify-between gap-3 px-5 pt-4 pb-3">
              <h2 className="text-muted-foreground text-[11px] font-medium tracking-wide uppercase">Exactly what was sent</h2>
              <span className="text-muted-foreground text-xs tabular-nums">
                {r.renderedBody?.length ?? 0} characters{r.segments ? ` · ${r.segments} ${r.segments === 1 ? 'segment' : 'segments'}` : ''}
              </span>
            </div>
            <div className="grid gap-5 px-5 pb-5 md:grid-cols-[minmax(0,1fr)_auto] md:items-start">
              <div className="grid gap-3">
                <div className="bg-muted/40 border p-4 text-[15px] leading-relaxed">
                  <TokenText body={r.renderedBody ?? ''} />
                </div>
                <p className="text-muted-foreground text-xs leading-relaxed">
                  Stored when the message was queued, before it went to the carrier{r.language && r.language !== m.sourceLanguage ? `, in ${r.language.toUpperCase()} for this person` : ''}. A variable still shown in braces went out as written.
                </p>
              </div>
              <div className="hidden justify-center md:flex" aria-hidden>
                <PhoneFrame time={clock}>
                  <SmsHeader name={job.name} number={m.fromNumber ?? job.phoneNumber?.display ?? 'No number'} />
                  <SmsThread>
                    <DayStamp>{fmtDateTime(r.sentAt ?? r.queuedAt)}</DayStamp>
                    <Incoming>{r.renderedBody ?? ''}</Incoming>
                  </SmsThread>
                  <SmsComposeBar />
                </PhoneFrame>
              </div>
            </div>
          </section>

          {/* ------------------------------------------------------------ the message it belongs to */}
          <section className="bg-card border px-5 py-4" aria-label="Message">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <h2 className="text-muted-foreground text-[11px] font-medium tracking-wide uppercase">Part of</h2>
              <MessageStatusBadge status={m.status} />
            </div>
            <Link to={`/jobs/${job.id}/history/${m.id}`} className="mt-2 block text-sm hover:underline">
              <TokenText body={m.renderedBody} className="line-clamp-2" />
            </Link>
            <p className="text-muted-foreground mt-1.5 text-xs">
              Sent {fmtDateTime(m.createdAt)} by {m.createdBy?.name ?? 'automation'} to {fmtNumber(m.recipientCount)} {m.recipientCount === 1 ? 'recipient' : 'recipients'}
              {m.counts ? ` · ${fmtNumber(m.counts.delivered)} delivered · ${fmtNumber(m.counts.failed)} failed` : ''}
            </p>
          </section>
        </div>

        <aside className="min-w-0 space-y-5 sm:space-y-6">
          {/* ------------------------------------------------------------ timeline */}
          <section className="bg-card border" aria-label="Timeline">
            <h2 className="text-muted-foreground px-5 pt-4 pb-3 text-[11px] font-medium tracking-wide uppercase">Timeline</h2>
            <ol className="px-5 pb-5">
              {steps.map((s, i) => (
                <li key={s.label} className="flex gap-3">
                  <div className="flex flex-col items-center">
                    <span className={cn('grid size-5 shrink-0 place-items-center rounded-full border', s.done ? (s.bad ? 'border-destructive bg-destructive text-white' : 'border-foreground bg-foreground text-background') : 'border-border bg-card text-muted-foreground')}>
                      {s.done ? s.bad ? <TriangleAlertIcon className="size-3" aria-hidden /> : <CheckIcon className="size-3" aria-hidden /> : <ClockIcon className="size-3" aria-hidden />}
                    </span>
                    {i < steps.length - 1 && <span className={cn('w-px flex-1', s.done ? 'bg-foreground/30' : 'bg-border')} aria-hidden />}
                  </div>
                  <div className={cn('min-w-0 pb-4', i === steps.length - 1 && 'pb-0')}>
                    <p className={cn('text-sm', s.done ? 'font-medium' : 'text-muted-foreground')}>{s.label}</p>
                    <p className="text-muted-foreground text-xs tabular-nums">{s.at ? fmtDateTime(s.at) : s.done ? '—' : 'Not yet'}</p>
                  </div>
                </li>
              ))}
            </ol>
          </section>

          {/* ------------------------------------------------------------ carrier record */}
          <section className="bg-card border" aria-label="Carrier record">
            <div className="flex items-center justify-between gap-3 px-5 pt-4 pb-3">
              <h2 className="text-muted-foreground text-[11px] font-medium tracking-wide uppercase">Carrier record</h2>
              <RadioIcon className="text-muted-foreground size-3.5" aria-hidden />
            </div>
            <dl className="divide-y border-t text-sm">
              <Fact label="Provider">
                {providerLabel(r.provider)}
                {r.provider === 'mock' && <span className="text-muted-foreground block text-xs">Simulated sends (development). Nothing reached a real phone.</span>}
                {r.provider === 'twilio' && <span className="text-muted-foreground block text-xs">Look the message SID up in the Twilio console for the carrier's own log.</span>}
              </Fact>
              <Fact label="From">
                <span className="tabular-nums">{m.fromNumber ?? '—'}</span>
              </Fact>
              <Fact label="To">
                <span className="tabular-nums">{r.phone}</span>
              </Fact>
              <Fact label="Account">
                <Mono value={r.providerAccountSid} />
              </Fact>
              <Fact label="Message SID">
                <Mono value={r.providerMessageId} />
              </Fact>
              <Fact label="Attempts">{fmtNumber(r.attempts)}</Fact>
              <Fact label="Receipt id">
                <Mono value={r.id} />
              </Fact>
            </dl>
          </section>
        </aside>
      </div>
    </div>
  );
}

function Fact({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="grid grid-cols-[6.5rem_minmax(0,1fr)] gap-3 px-5 py-2.5">
      <dt className="text-muted-foreground text-xs">{label}</dt>
      <dd className="min-w-0">{children}</dd>
    </div>
  );
}

/** A provider id: monospace, wrapped, with a copy control. */
function Mono({ value }: { value: string | null }) {
  const [copied, setCopied] = useState(false);
  if (!value) return <span className="text-muted-foreground">—</span>;
  return (
    <span className="flex items-start justify-between gap-2">
      <span className="font-mono text-xs break-all">{value}</span>
      <button
        type="button"
        className="text-muted-foreground hover:text-foreground -my-0.5 grid size-6 shrink-0 place-items-center"
        aria-label="Copy"
        onClick={async () => {
          try {
            await navigator.clipboard.writeText(value);
            setCopied(true);
            setTimeout(() => setCopied(false), 1500);
          } catch {
            /* ignore */
          }
        }}
      >
        {copied ? <CheckIcon className="text-success size-3.5" aria-hidden /> : <CopyIcon className="size-3.5" aria-hidden />}
      </button>
    </span>
  );
}

function CopyLink() {
  const [copied, setCopied] = useState(false);
  return (
    <Button
      variant="outline"
      size="sm"
      onClick={async () => {
        try {
          await navigator.clipboard.writeText(window.location.href);
          setCopied(true);
          setTimeout(() => setCopied(false), 1500);
        } catch {
          /* ignore */
        }
      }}
    >
      {copied ? <CheckIcon className="text-success" /> : <LinkIcon />}
      {copied ? 'Copied' : 'Copy link'}
    </Button>
  );
}

